import React, { useState, useRef, useEffect } from 'react';

/* Desktop Nav Dropdown -- extracted verbatim from App.jsx (C1 refactor,
   behavior-preserving). Rendered by BlitzHeader on desktop (one per nav group
   with type "dropdown"). Self-contained: useState/useRef/useEffect + native
   HTML + inline SVG.

   F1-v2 (2026-06-13): accepts `compact` prop from BlitzHeader.
   When compact=true: trigger button uses 11px font, 4px/6px padding, 3px icon-gap,
   10×10 chevron — matches the compressed single-type nav items in BlitzHeader. */
export function NavDropdown({ label, icon, items, activePage, userAccess, onNav, accentColor, compact }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const hasActive = items.some(i => i.key === activePage);
  const visibleItems = items.filter(i => (userAccess || []).includes(i.key));

  // Hook must be called unconditionally BEFORE any early return (react-hooks/rules-of-hooks).
  useEffect(() => {
    if (visibleItems.length === 0) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [visibleItems.length]);

  if (visibleItems.length === 0) return null;

  // Compact: smaller font/padding/gap to compress nav width ~606px → ~480px
  const btnPad = compact ? "4px 6px" : "5px 12px";
  const btnFont = compact ? 11 : 13;
  const iconGap = compact ? 3 : 5;
  const chevronSize = compact ? 10 : 12;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(!open)}
        style={{
          display: "flex", alignItems: "center", gap: iconGap, padding: btnPad, borderRadius: 8, fontSize: btnFont, fontWeight: 700, cursor: "pointer",
          background: hasActive ? `${accentColor}15` : "transparent",
          border: hasActive ? `1.5px solid ${accentColor}40` : "1.5px solid transparent",
          color: hasActive ? accentColor : "#64748B",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        onMouseEnter={e => { if (!hasActive) { e.currentTarget.style.background = "rgba(100,116,139,0.06)"; e.currentTarget.style.color = accentColor; } }}
        onMouseLeave={e => { if (!hasActive) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#64748B"; } }}
      >
        <span style={{ fontSize: compact ? 12 : 14 }}>{icon}</span>
        {label}
        <svg width={chevronSize} height={chevronSize} viewBox="0 0 12 12" fill="none" style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}>
          <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 200,
          background: "#FFFFFF", borderRadius: 16, border: "1px solid rgba(99,102,241,0.12)", boxShadow: "0 12px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
          padding: "6px", zIndex: 200, animation: "fadeUp 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        }}>
          {visibleItems.map(pg => {
            const isActive = activePage === pg.key;
            return (
              <button key={pg.key} onClick={() => { onNav(pg.key); setOpen(false); }}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 14px",
                  border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: isActive ? 700 : 500, textAlign: "left",
                  background: isActive ? `${pg.color}12` : "transparent",
                  color: isActive ? pg.color : "#334155",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#F8FAFC"; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: isActive ? pg.color : "#CBD5E1", transition: "all 0.15s" }} />
                {pg.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
